import type { ImportResult, ImportedEdge, ImportedResource } from '../../../lib/cloud-importers';

type CompassGraphNodeInput = {
  id: string;
  type: string;
  label?: string | null;
  props?: unknown;
};

type CompassGraphEdgeInput = {
  id?: string;
  type: string;
  from: string;
  to: string;
  props?: unknown;
};

export function compassGraphToImportResult(
  nodes: ReadonlyArray<CompassGraphNodeInput>,
  edges: ReadonlyArray<CompassGraphEdgeInput>,
): ImportResult {
  const resources: ImportedResource[] = nodes
    .filter((node) => !node.type.startsWith('_'))
    .map((node) => {
      const props = node.props && typeof node.props === 'object'
        ? propsAsRecord(node.props)
        : {};
      const tags = props.tags && typeof props.tags === 'object'
        ? props.tags as Record<string, string>
        : undefined;

      return {
        id: node.id,
        manifestId: typeof props._manifestId === 'string' ? props._manifestId : node.type,
        name: String(node.label ?? props.name ?? node.id),
        region: typeof props.region === 'string' ? props.region : undefined,
        az: typeof props.az === 'string' ? props.az : undefined,
        accountId: typeof props.accountId === 'string' ? props.accountId : undefined,
        hostingProvider: typeof props.hostingProvider === 'string' ? props.hostingProvider : undefined,
        runtimeProvider: typeof props.runtimeProvider === 'string' ? props.runtimeProvider : undefined,
        dialectId: typeof props.dialectId === 'string' ? props.dialectId : undefined,
        providerLocalType: typeof props.providerLocalType === 'string' ? props.providerLocalType : undefined,
        parentId: typeof props.parentId === 'string' ? props.parentId : undefined,
        tags,
        attributes: {
          ...props,
          compassGraphNodeId: node.id,
        },
      };
    });

  const resourceIds = new Set(resources.map((resource) => resource.id));
  const importedEdges: ImportedEdge[] = edges
    .filter((edge) => resourceIds.has(edge.from) && resourceIds.has(edge.to))
    .map((edge, index) => ({
      id: edge.id ?? `compass-edge-${index}`,
      from: edge.from,
      to: edge.to,
      kind: toImportedEdgeKind(edge.type),
      attributes: edge.props && typeof edge.props === 'object'
        ? propsAsRecord(edge.props)
        : {},
    }));

  return {
    format: 'provider-catalog',
    sourceLabel: 'Compass Explorer view',
    ...deriveResultProviderMetadata(resources),
    resources,
    edges: importedEdges,
    warnings: [],
  };
}

function propsAsRecord(value: object): Record<string, unknown> {
  return value as Record<string, unknown>;
}

/**
 * Map a Compass-graph edge `type` to the importer's coarse `ImportedEdgeKind`.
 *
 * Inputs may carry either the rich canvas vocabulary (`allows_traffic_to`,
 * `assumes_identity`, `encrypted_by`, `load_balances_to`, `contained_in`)
 * OR the universal federation-interop vocabulary from `lib/graph/types.ts`
 * (`contains`, `depends_on`, `data_flow`, `association`, `controlled_by`,
 * `evidenced_by`, `routes_to`, `assumes`). Both must round-trip without
 * silent semantic loss — falling everything into `data_flow` would drop
 * IAM/governance/encryption signals that downstream lenses depend on.
 */
function toImportedEdgeKind(type: string): ImportedEdge['kind'] {
  switch (type) {
    // Containment family
    case 'contains':
    case 'contained_in':
    case 'containment':
    case 'in-vpc':
    case 'in-subnet':
      return 'containment';

    // Network-access family. `depends_on` is the universal generic
    // dependency edge — it does NOT belong here; route to data_flow below.
    case 'sg_access':
    case 'allows_traffic_to':
    case 'association':
      return 'sg_access';

    // Routing / load balancing family
    case 'routes_to':
    case 'routing':
    case 'load_balances_to':
      return 'routing';

    // Identity family
    case 'assumes':
    case 'identity':
    case 'assumes_identity':
      return 'identity';

    // Governance / control / encryption / observability family
    case 'encrypted_by':
    case 'logs_to':
    case 'governance':
    case 'controlled_by':
    case 'evidenced_by':
      return 'governance';

    // Data movement (explicit + the generic dependency edge)
    case 'data_flow':
    case 'depends_on':
    case 'reads_from':
    case 'writes_to':
      return 'data_flow';

    default:
      return 'data_flow';
  }
}

function deriveResultProviderMetadata(resources: ImportedResource[]): Pick<ImportResult, 'hostingProvider' | 'runtimeProvider' | 'dialectId'> {
  const firstDialect = firstUnique(resources.map((resource) => resource.dialectId));
  const firstHost = firstUnique(resources.map((resource) => resource.hostingProvider));
  const firstRuntime = firstUnique(resources.map((resource) => resource.runtimeProvider));
  return {
    ...(firstHost ? { hostingProvider: firstHost } : {}),
    ...(firstRuntime ? { runtimeProvider: firstRuntime } : {}),
    ...(firstDialect ? { dialectId: firstDialect } : {}),
  };
}

function firstUnique(values: Array<string | undefined>): string | null {
  const set = new Set(values.filter((value): value is string => Boolean(value)));
  return set.size === 1 ? Array.from(set)[0] : null;
}
